import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../finance/auth/AuthContext';
import { getMyMeetings, getMeetingById, deleteMeeting } from '../../api/meetings';
import { toast } from 'react-hot-toast';
import { FiArrowLeft, FiClock, FiMapPin, FiVideo, FiUsers, FiEdit2, FiTrash2, FiCalendar, FiUser } from 'react-icons/fi';
import MeetingFormModal from './MeetingFormModal';
import ConfirmModal from '../shared/ConfirmModal';

const MeetingDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, depts, isSuperadmin, username } = useAuth();
  
  const isAdmin = String(role).toUpperCase() === 'ADMIN' || isSuperadmin;
  const hasSalesDept = depts?.some((d) => String(d).toUpperCase() === 'SALES') || isAdmin;
  const canCreateEdit = hasSalesDept;

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMeeting();
  }, [id]);

  const fetchMeeting = async () => {
    try {
      setLoading(true);
      const meetingData = await getMeetingById(id);
      if (meetingData) {
        setMeeting(meetingData);
      } else {
        toast.error('Meeting not found');
        navigate('/calendar');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load meeting details');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteMeeting(id);
      toast.success('Meeting deleted');
      setDeleteConfirmOpen(false);
      navigate('/calendar');
    } catch (error) {
      toast.error('Failed to delete meeting');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) 
           + ' at ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeOnly = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 text-slate-500">Loading meeting details...</div>
    );
  }

  if (!meeting) return null;

  return (
    <>
      <div className="max-w-4xl mx-auto p-0 sm:p-0 space-y-4">
        
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 sm:p-5 bg-gradient-to-r from-blue-900 to-slate-900 text-white relative">
            <button 
              onClick={() => navigate('/calendar')}
              className="absolute top-6 left-6 text-white/70 hover:text-white flex items-center gap-2 text-sm transition-colors"
            >
              <FiArrowLeft /> Back
            </button>

            <div className="mt-10 md:flex justify-between items-start gap-4">
              <div>
                <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-3
                  ${meeting.status === 'SCHEDULED' ? 'bg-blue-500/20 text-blue-100 border border-blue-400/30' : 
                    meeting.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30' : 'bg-slate-500/20 text-slate-200 border border-slate-400/30'}
                `}>
                  {meeting.status}
                </span>
                <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full mb-3 ml-2 bg-slate-700/50 text-slate-200 border border-slate-600/50">
                  {meeting.meetingType} • {meeting.meetingMode}
                </span>
                <h1 className="text-2xl sm:text-3xl font-bold mb-2">{meeting.title}</h1>
                <p className="text-blue-100/80 text-lg flex items-center gap-2">
                  <FiCalendar /> {formatDateTime(meeting.meetingStart)}
                </p>
              </div>
              
              {canCreateEdit && (
                <div className="mt-6 md:mt-0 flex gap-3">
                  <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg transition-colors flex items-center gap-2 font-medium">
                    <FiEdit2 size={16} /> Edit
                  </button>
                  <button onClick={() => setDeleteConfirmOpen(true)} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/40 text-red-100 border border-red-500/30 rounded-lg transition-colors flex items-center gap-2 font-medium">
                    <FiTrash2 size={16} /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50 flex flex-wrap gap-4 text-sm text-slate-700">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 text-blue-700 rounded-lg"><FiClock /></div>
              <div>
                <p className="text-slate-500 text-xs font-semibold uppercase">Duration</p>
                <p className="font-medium">{formatTimeOnly(meeting.meetingStart)} - {formatTimeOnly(meeting.meetingEnd)}</p>
              </div>
            </div>
            
            {meeting.location && (
              <div className="flex items-center gap-2">
                <div className="p-2 bg-slate-200 text-slate-700 rounded-lg"><FiMapPin /></div>
                <div>
                  <p className="text-slate-500 text-xs font-semibold uppercase">Location</p>
                  <p className="font-medium">{meeting.location}</p>
                </div>
              </div>
            )}
            {meeting.meetingLink && (
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 text-blue-700 rounded-lg"><FiVideo /></div>
                <div>
                  <p className="text-slate-500 text-xs font-semibold uppercase">Meeting Link</p>
                  <a href={meeting.meetingLink} target="_blank" rel="noreferrer" className="font-medium text-blue-600 hover:underline">Join Now</a>
                </div>
              </div>
            )}
            
            {meeting.organizerName && (
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg"><FiUser /></div>
                <div>
                  <p className="text-slate-500 text-xs font-semibold uppercase">Organizer</p>
                  <p className="font-medium text-slate-800">{meeting.organizerName}</p>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 sm:p-5">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Description</h3>
            <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
              {meeting.description || 'No description provided.'}
            </p>
          </div>
        </div>

        {/* Attendees Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-5">
          <div className="flex items-center gap-3 mb-4">
            <FiUsers className="text-xl text-blue-600" />
            <h3 className="text-xl font-bold text-slate-800">Attendees ({meeting.attendees?.length || 0})</h3>
          </div>
          
          {meeting.attendees && meeting.attendees.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {meeting.attendees.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-blue-200 hover:shadow-sm transition-all bg-slate-50 hover:bg-white group">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0 border border-blue-200">
                    {a.name.charAt(0)}
                  </div>
                  <div className="overflow-hidden">
                    <p className="font-semibold text-slate-800 truncate group-hover:text-blue-700 transition-colors">{a.name}</p>
                    <p className="text-xs text-slate-500 truncate">{a.department || 'Employee'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200">
              No attendees listed.
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <MeetingFormModal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)} 
          meeting={meeting}
          onSuccess={fetchMeeting}
        />
      )}
      
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={executeDelete}
        title="Delete Meeting"
        message="Are you sure you want to delete this meeting? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        loading={isDeleting}
      />
    </>
  );
};

export default MeetingDetailsPage;
